import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { ArrowLeft, Dumbbell, BookOpen, Calendar, ChevronRight, ChevronDown, ChevronUp, Lock, CheckCircle, Play, Info, Clock, Target, Flame, Heart, Zap, History, Star } from 'lucide-react';
import axios from 'axios';

import { IMAGES } from './fitness/images';
import { THEORY_SECTIONS } from './fitness/theory';
import { EXERCISES } from './fitness/exercises';
import { PROGRAMS } from './fitness/programs';

const API = process.env.REACT_APP_BACKEND_URL;

export default function FitnessPage() {
  const navigate = useNavigate();
  const { hasAccess, token } = useAuth();
  const [activeTab, setActiveTab] = useState('theory');
  const [expandedTheory, setExpandedTheory] = useState('principles');
  const [selectedProgram, setSelectedProgram] = useState(null);
  const [selectedExercise, setSelectedExercise] = useState(null);
  
  // Träningslogg-state
  const [isTraining, setIsTraining] = useState(false);
  const [trainingDay, setTrainingDay] = useState(null);
  const [exerciseLogs, setExerciseLogs] = useState({});
  const [workoutRating, setWorkoutRating] = useState(0);
  const [workoutNotes, setWorkoutNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [trainingStartTime, setTrainingStartTime] = useState(null);

  const startTraining = (program, day) => {
    setIsTraining(true);
    setTrainingDay({ program, day });
    setTrainingStartTime(Date.now());
    setExerciseLogs({});
    setWorkoutRating(0);
    setWorkoutNotes('');
  };

  const updateExerciseLog = (exerciseId, setIndex, field, value) => {
    setExerciseLogs(prev => {
      const exerciseData = prev[exerciseId] || { sets: [], notes: '' };
      const sets = [...(exerciseData.sets || [])];
      
      if (!sets[setIndex]) {
        sets[setIndex] = { reps: '', weight: '' };
      }
      sets[setIndex][field] = value;
      
      return {
        ...prev,
        [exerciseId]: { ...exerciseData, sets }
      };
    });
  };

  const updateExerciseNotes = (exerciseId, notes) => {
    setExerciseLogs(prev => ({
      ...prev,
      [exerciseId]: { ...(prev[exerciseId] || { sets: [] }), notes }
    }));
  };

  const saveWorkout = async () => {
    if (!trainingDay) return;
    
    setSaving(true);
    try {
      const duration = trainingStartTime 
        ? Math.round((Date.now() - trainingStartTime) / 60000)
        : null;
      
      const exercises = trainingDay.day.exercises.map(ex => {
        const exercise = EXERCISES[ex.id];
        const log = exerciseLogs[ex.id] || { sets: [], notes: '' };
        
        return {
          exercise_id: ex.id,
          exercise_name: exercise?.name || ex.id,
          sets_completed: log.sets.filter(s => s.reps || s.weight).map(s => ({
            reps: parseInt(s.reps) || 0,
            weight: parseFloat(s.weight) || 0
          })),
          notes: log.notes || null
        };
      }).filter(ex => ex.sets_completed.length > 0);

      if (exercises.length === 0) {
        alert('Logga minst en övning innan du sparar!');
        setSaving(false);
        return;
      }

      await axios.post(`${API}/api/workouts`, {
        program_id: trainingDay.program.id,
        program_name: trainingDay.program.title,
        day_name: trainingDay.day.name,
        exercises,
        duration_minutes: duration,
        notes: workoutNotes || null,
        rating: workoutRating || null
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      alert('Träningspass sparat! 💪');
      setIsTraining(false);
      setTrainingDay(null);
      setSelectedProgram(null);
    } catch (error) {
      console.error('Error saving workout:', error);
      alert('Kunde inte spara träningspasset');
    } finally {
      setSaving(false);
    }
  };

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50" data-testid="fitness-page">
        <header className="border-b bg-white sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Träning</h1>
          </div>
        </header>
        <main className="max-w-4xl mx-auto px-6 py-12 text-center">
          <Lock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
          <h2 className="font-heading text-2xl font-semibold mb-2">Uppgradera för tillgång</h2>
          <p className="text-muted-foreground mb-6">Träningsprogram och övningar ingår i premium.</p>
          <Button onClick={() => navigate('/subscription')} className="rounded-full">Uppgradera nu</Button>
        </main>
      </div>
    );
  }

  // Visa övningsdetalj
  if (selectedExercise) {
    const ex = EXERCISES[selectedExercise];
    return (
      <div className="min-h-screen bg-gray-50" data-testid="fitness-page">
        <header className="border-b bg-white sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedExercise(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">{ex.name}</h1>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          <img src={ex.image} alt={ex.name} className="w-full h-56 object-cover rounded-2xl" />
          
          <div className="flex flex-wrap gap-2">
            <span className="px-3 py-1 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium">{ex.muscle}</span>
            <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">{ex.equipment}</span>
          </div>

          <p className="text-gray-600">{ex.description}</p>

          <div className="bg-white rounded-2xl border p-5">
            <h3 className="font-semibold text-lg mb-4 flex items-center gap-2">
              <Play className="w-5 h-5 text-emerald-500" />
              Utförande
            </h3>
            <ol className="space-y-3">
              {ex.steps.map((step, i) => (
                <li key={i} className="flex gap-4">
                  <span className="w-7 h-7 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center font-bold text-sm flex-shrink-0">
                    {i + 1}
                  </span>
                  <span className="text-gray-700">{step}</span>
                </li>
              ))}
            </ol>
          </div>

          <div className="bg-amber-50 rounded-2xl p-5 border border-amber-200">
            <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
              <Info className="w-5 h-5" />
              Tips
            </h4>
            <p className="text-amber-700">{ex.tips}</p>
          </div>
        </main>
      </div>
    );
  }

  // Visa programdetalj eller träningsläge
  if (selectedProgram) {
    const program = PROGRAMS.find(p => p.id === selectedProgram);
    
    // Om vi är i träningsläge, visa loggningsvy
    if (isTraining && trainingDay) {
      const currentDay = trainingDay.day;
      return (
        <div className="min-h-screen bg-gray-50" data-testid="fitness-page">
          <header className="border-b bg-white sticky top-0 z-50">
            <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-4">
                <Button variant="ghost" onClick={() => {
                  if (window.confirm('Avsluta träning? Osparade ändringar går förlorade.')) {
                    setIsTraining(false);
                    setTrainingDay(null);
                  }
                }} className="rounded-full">
                  <ArrowLeft className="w-5 h-5" />
                </Button>
                <div>
                  <h1 className="font-heading text-lg font-semibold">{currentDay.name}</h1>
                  <p className="text-xs text-emerald-600 font-medium flex items-center gap-1">
                    <Flame className="w-3 h-3" /> Träning pågår
                  </p>
                </div>
              </div>
            </div>
          </header>
          
          <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
            <div className="bg-emerald-50 rounded-2xl p-4 border border-emerald-200 text-center">
              <p className="text-emerald-700 text-sm">
                Logga dina vikter och reps för varje övning. Klicka på "Spara pass" när du är klar!
              </p>
            </div>
            
            {currentDay.exercises.map((ex, exIndex) => {
              const exercise = EXERCISES[ex.id];
              const exerciseLog = exerciseLogs[ex.id] || { sets: [], notes: '' };
              
              return (
                <div key={exIndex} className="bg-white rounded-2xl border overflow-hidden">
                  <div className="px-4 py-3 border-b flex items-center gap-3">
                    <img 
                      src={exercise.image} 
                      alt={exercise.name} 
                      className="w-12 h-12 rounded-xl object-cover"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-sm">{exercise.name}</p>
                      <p className="text-xs text-muted-foreground">{ex.sets} set × {ex.reps}</p>
                    </div>
                  </div>
                  
                  <div className="p-4 space-y-2">
                    {Array.from({ length: ex.sets }).map((_, setIndex) => (
                      <div key={setIndex} className="flex items-center gap-2">
                        <span className="w-8 text-xs text-muted-foreground">Set {setIndex + 1}</span>
                        <input
                          type="number"
                          placeholder="Reps"
                          className="flex-1 px-3 py-2 text-sm border rounded-lg"
                          value={exerciseLog.sets[setIndex]?.reps || ''}
                          onChange={(e) => updateExerciseLog(ex.id, setIndex, 'reps', e.target.value)}
                        />
                        <input
                          type="number"
                          placeholder="Vikt (kg)"
                          className="flex-1 px-3 py-2 text-sm border rounded-lg"
                          value={exerciseLog.sets[setIndex]?.weight || ''}
                          onChange={(e) => updateExerciseLog(ex.id, setIndex, 'weight', e.target.value)}
                        />
                      </div>
                    ))}
                    
                    <input
                      type="text"
                      placeholder="Anteckningar för övningen..."
                      className="w-full px-3 py-2 text-sm border rounded-lg mt-2"
                      value={exerciseLog.notes || ''}
                      onChange={(e) => updateExerciseNotes(ex.id, e.target.value)}
                    />
                  </div>
                </div>
              );
            })}
            
            {/* Rating och anteckningar */}
            <div className="bg-white rounded-2xl border p-4 space-y-4">
              <div>
                <p className="text-sm font-medium mb-2">Hur var passet?</p>
                <div className="flex gap-2">
                  {[1, 2, 3, 4, 5].map((rating) => (
                    <button
                      key={rating}
                      onClick={() => setWorkoutRating(rating)}
                      className={`w-10 h-10 rounded-full flex items-center justify-center transition-all ${
                        workoutRating >= rating 
                          ? 'bg-yellow-400 text-white' 
                          : 'bg-slate-100 text-slate-400 hover:bg-slate-200'
                      }`}
                    >
                      <Star className={`w-5 h-5 ${workoutRating >= rating ? 'fill-white' : ''}`} />
                    </button>
                  ))}
                </div>
              </div>
              
              <div>
                <p className="text-sm font-medium mb-2">Övriga anteckningar</p>
                <textarea
                  placeholder="Hur kändes det? Något speciellt att notera?"
                  className="w-full px-3 py-2 text-sm border rounded-lg min-h-[80px]"
                  value={workoutNotes}
                  onChange={(e) => setWorkoutNotes(e.target.value)}
                />
              </div>
            </div>
            
            {/* Spara-knapp */}
            <Button 
              onClick={saveWorkout}
              disabled={saving}
              className="w-full rounded-full bg-emerald-600 hover:bg-emerald-700"
            >
              {saving ? 'Sparar...' : '✓ Spara träningspass'}
            </Button>
          </main>
        </div>
      );
    }
    
    // Normal programvy med "Starta träning"-knappar
    return (
      <div className="min-h-screen bg-gray-50" data-testid="fitness-page">
        <header className="border-b bg-white sticky top-0 z-50">
          <div className="max-w-4xl mx-auto px-6 py-4 flex items-center gap-4">
            <Button variant="ghost" onClick={() => setSelectedProgram(null)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="font-heading text-lg font-semibold">{program.title}</h1>
              <p className="text-xs text-muted-foreground">{program.frequency}x/vecka • {program.duration}</p>
            </div>
          </div>
        </header>
        <main className="max-w-2xl mx-auto px-4 py-6 space-y-6">
          <div className="bg-emerald-50 rounded-2xl p-5 border border-emerald-200">
            <p className="text-emerald-800 mb-3">{program.description}</p>
            <p className="text-sm text-emerald-700 font-medium">{program.schedule}</p>
          </div>

          {program.days.map((day, dayIndex) => (
            <div key={dayIndex} className="bg-white rounded-2xl border overflow-hidden">
              <div className="bg-slate-50 px-5 py-4 border-b flex items-center justify-between">
                <div>
                  <h3 className="font-semibold">{day.name}</h3>
                  <p className="text-xs text-muted-foreground">{day.exercises.length} övningar</p>
                </div>
                <Button 
                  size="sm" 
                  onClick={() => startTraining(program, day)}
                  className="rounded-full bg-emerald-600 hover:bg-emerald-700"
                >
                  <Play className="w-4 h-4 mr-1" /> Starta
                </Button>
              </div>
              <div className="divide-y">
                {day.exercises.map((ex, exIndex) => {
                  const exercise = EXERCISES[ex.id];
                  return (
                    <button
                      key={exIndex}
                      onClick={() => setSelectedExercise(ex.id)}
                      className="w-full px-5 py-4 flex items-center gap-4 text-left hover:bg-gray-50 transition-colors"
                    >
                      <img 
                        src={exercise.image} 
                        alt={exercise.name} 
                        className="w-14 h-14 rounded-xl object-cover"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium">{exercise.name}</p>
                        <p className="text-sm text-muted-foreground">{exercise.muscle}</p>
                      </div>
                      <div className="text-right">
                        <p className="font-mono text-sm bg-slate-100 px-2 py-1 rounded">{ex.sets}x{ex.reps}</p>
                        <p className="text-xs text-muted-foreground mt-1">Vila: {ex.rest}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          ))}

          <div className="bg-blue-50 rounded-2xl p-5 border border-blue-200">
            <h4 className="font-semibold text-blue-800 mb-2">💡 Kom ihåg</h4>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• Värm alltid upp 5-10 min innan</li>
              <li>• Börja med lägre vikt för att lära tekniken</li>
              <li>• Öka vikten gradvis när du klarar alla reps</li>
              <li>• Stretcha efter passet</li>
            </ul>
          </div>
        </main>
      </div>
    );
  }

  // Huvudvy
  return (
    <div className="min-h-screen bg-gray-50" data-testid="fitness-page">
      <header className="border-b bg-white sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/category/health')} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <h1 className="font-heading text-lg font-semibold">Träning</h1>
          </div>
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => navigate('/workout-history')}
            className="rounded-full"
          >
            <History className="w-4 h-4 mr-1" />
            Historik
          </Button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Intro */}
        <div className="text-center space-y-2">
          <Dumbbell className="w-12 h-12 mx-auto text-emerald-500" />
          <h2 className="font-heading text-2xl font-semibold">Din träningsguide</h2>
          <p className="text-muted-foreground">Lär dig grunderna, hitta övningar och välj program</p>
        </div>

        {/* Tabs */}
        <div className="flex gap-2 justify-center flex-wrap">
          <Button 
            variant={activeTab === 'theory' ? 'default' : 'outline'} 
            onClick={() => setActiveTab('theory')}
            className="rounded-full"
            size="sm"
          >
            <BookOpen className="w-4 h-4 mr-2" />
            Teori
          </Button>
          <Button 
            variant={activeTab === 'exercises' ? 'default' : 'outline'} 
            onClick={() => setActiveTab('exercises')}
            className="rounded-full"
            size="sm"
          >
            <Dumbbell className="w-4 h-4 mr-2" />
            Övningar
          </Button>
          <Button 
            variant={activeTab === 'programs' ? 'default' : 'outline'} 
            onClick={() => setActiveTab('programs')}
            className="rounded-full"
            size="sm"
          >
            <Calendar className="w-4 h-4 mr-2" />
            Program
          </Button>
        </div>

        {/* TEORI */}
        {activeTab === 'theory' && (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground text-center mb-4">
              Läs igenom dessa avsnitt för att förstå hur träning fungerar
            </p>
            {THEORY_SECTIONS.map((section) => (
              <div key={section.id} className="bg-white rounded-2xl border overflow-hidden">
                <button
                  onClick={() => setExpandedTheory(expandedTheory === section.id ? null : section.id)}
                  className="w-full px-5 py-4 flex items-center justify-between text-left"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-2xl">{section.icon}</span>
                    <h3 className="font-semibold">{section.title}</h3>
                  </div>
                  {expandedTheory === section.id ? (
                    <ChevronUp className="w-5 h-5 text-muted-foreground" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-muted-foreground" />
                  )}
                </button>
                {expandedTheory === section.id && (
                  <div className="px-5 pb-5 border-t pt-4">
                    <div className="prose prose-sm max-w-none text-gray-600 whitespace-pre-line">
                      {section.content}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* ÖVNINGAR */}
        {activeTab === 'exercises' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Klicka på en övning för att se hur den utförs
            </p>
            
            {/* Gruppera övningar */}
            {[
              { title: 'Ben & Rumpa', ids: ['squat', 'romanianDeadlift', 'lunge', 'legPress', 'legCurl', 'gluteBridge', 'calfRaise'] },
              { title: 'Bröst', ids: ['benchPress', 'pushUp', 'inclineBenchPress', 'chestFly', 'dips'] },
              { title: 'Rygg', ids: ['latPulldown', 'barbellRow', 'dumbbellRow', 'seatedRow', 'pullUp', 'facePull'] },
              { title: 'Axlar', ids: ['overheadPress', 'lateralRaise', 'rearDeltFly', 'frontRaise'] },
              { title: 'Armar', ids: ['bicepCurl', 'hammerCurl', 'tricepPushdown', 'tricepExtension'] },
              { title: 'Core / Mage', ids: ['plank', 'sidePlank', 'crunch', 'legRaise', 'russianTwist'] },
            ].map((group) => (
              <div key={group.title}>
                <h3 className="font-semibold text-sm text-muted-foreground mb-2 px-1">{group.title}</h3>
                <div className="grid grid-cols-2 gap-3">
                  {group.ids.map((id) => {
                    const ex = EXERCISES[id];
                    if (!ex) return null;
                    return (
                      <button
                        key={id}
                        onClick={() => setSelectedExercise(id)}
                        className="bg-white rounded-xl border overflow-hidden text-left hover:shadow-md transition-all group"
                      >
                        <img src={ex.image} alt={ex.name} className="w-full h-24 object-cover" />
                        <div className="p-3">
                          <h4 className="font-medium text-sm group-hover:text-emerald-600">{ex.name}</h4>
                          <p className="text-xs text-muted-foreground truncate">{ex.muscle}</p>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {/* PROGRAM */}
        {activeTab === 'programs' && (
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground text-center">
              Välj ett program baserat på hur många dagar du kan träna
            </p>
            
            {PROGRAMS.map((program) => (
              <button
                key={program.id}
                onClick={() => setSelectedProgram(program.id)}
                className="w-full bg-white rounded-2xl border p-5 text-left hover:border-emerald-300 hover:shadow-md transition-all group"
              >
                <div className="flex items-start gap-4">
                  <div className="w-16 h-16 rounded-xl bg-emerald-100 flex flex-col items-center justify-center group-hover:bg-emerald-200 transition-colors">
                    <span className="text-2xl font-bold text-emerald-600">{program.frequency}</span>
                    <span className="text-xs text-emerald-600">dagar</span>
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold group-hover:text-emerald-600 transition-colors">{program.title}</h3>
                    <p className="text-sm text-muted-foreground">{program.subtitle}</p>
                    <p className="text-sm text-gray-600 mt-2">{program.description}</p>
                    <div className="flex gap-3 mt-3 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {program.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <Target className="w-3 h-3" /> {program.level}
                      </span>
                    </div>
                  </div>
                  <ChevronRight className="w-5 h-5 text-muted-foreground group-hover:text-emerald-500 mt-4" />
                </div>
              </button>
            ))}
          </div>
        )}

        <p className="text-xs text-muted-foreground text-center pt-4">
          ⚕️ Informationen ersätter inte medicinsk rådgivning. Kontakta sjukvården vid hälsoproblem.
        </p>
      </main>
    </div>
  );
}
